import { useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import SaintForm from "../components/SaintForm";
import Toast from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { slugify } from "../lib/slugify";
import { createSaint } from "../services/saintsRepo";
import { useAuth } from "../hooks/useAuth";

export default function Request() {
  const navigate = useNavigate();
  const { message: toast, show: showToast, clear: clearToast } = useToast(2500);
  const { user, loading, admin, adminLoading, email } = useAuth();

  useEffect(() => {
    document.title = "Dulia: Request Profile";
  }, []);

  const navTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
    };
  }, []);

  const gateLoading = loading || adminLoading;

  // Signed-in non-admins can request
  const canRequest = !!user && !admin && !gateLoading;

  async function handleRequest(payload, meta) {
    if (!canRequest) return { ok: false, message: "Not authorized." };

    const desiredId = String(meta?.docId || "").trim();
    const fallback = slugify(payload?.name);
    const slug = desiredId || fallback;

    if (!slug) return { ok: false, message: "Name is required." };

    await createSaint(slug, { ...payload, approved: false, requestedBy: email });

    showToast(`Requested: saints/${slug}`);

    if (navTimerRef.current) clearTimeout(navTimerRef.current);
    navTimerRef.current = setTimeout(() => {
      navigate('/home');
    }, 650);

    return { ok: true, message: `Requested: saints/${slug}` };
  }

  if (gateLoading) return <div>Loading…</div>;

  if (!user) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-bold">Request Profile</h1>
        <div className="rounded border bg-white p-4 text-sm text-gray-700 dark:bg-gray-950 dark:border-gray-800 dark:text-gray-200">
          You must be signed in to request a profile.
        </div>
        <Link className="underline" to="/search">
          Back to search
        </Link>
      </div>
    );
  }

  if (admin) {
    return (
      <div className="space-y-3">
        <h1 className="text-2xl font-bold">Request Profile</h1>
        <div className="rounded border bg-white p-4 text-sm text-gray-700 dark:bg-gray-950 dark:border-gray-800 dark:text-gray-200">
          You’re an admin—use Create Profile instead.
        </div>
        <Link className="underline" to="/admin/new">
          Go to Create Profile
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Toast message={toast} onClose={clearToast} />

      <h1 className="text-2xl font-bold">Request Profile</h1>

      <SaintForm
        mode="create"
        initialValues={{
          stage: "Saint",
          isPope: false,
          feastMonth: "",
          feastDay: "",
          beatifiedISO: "",
          canonizedISO: "",
          tags: [],
          patronage: [],
          categories: [],
          quotes: [],
          prayers: [],
          gallery: [],
          contributors: [],
        }}
        onSubmit={handleRequest}
        submitLabel="Request"
      />
    </div>
  );
}
